import { z } from "zod";
import {
  DecisionSchema,
  PresetSummarySchema,
  StrategyDslSchema,
  StrategyExportSchema,
  StrategyPnlSchema,
  StrategyRunSchema,
  StrategySchema,
} from "../entities";
import { ExecutionModeSchema, StrategyStatusSchema } from "../enums";
import { MoneySchema, pageOf } from "../primitives";

/** `GET /api/strategies` (optional status filter). */
export const ListStrategiesQuerySchema = z.object({
  status: StrategyStatusSchema.optional(),
});
export type ListStrategiesQuery = z.infer<typeof ListStrategiesQuerySchema>;

export const ListStrategiesResponseSchema = z.object({
  strategies: z.array(StrategySchema),
});
export type ListStrategiesResponse = z.infer<typeof ListStrategiesResponseSchema>;

/** `POST /api/strategies`. Runtime bindings default to fail-closed dry-run. */
export const CreateStrategySchema = z.object({
  name: z.string().min(1),
  dsl: StrategyDslSchema,
  executionMode: ExecutionModeSchema,
  accountSeq: z.string().optional(),
  budget: MoneySchema.optional(),
  dryRun: z.boolean().optional(),
});
export type CreateStrategy = z.infer<typeof CreateStrategySchema>;

/** `PUT /api/strategies/:id`. */
export const UpdateStrategySchema = z
  .object({
    name: z.string().min(1),
    dsl: StrategyDslSchema,
    executionMode: ExecutionModeSchema,
    accountSeq: z.string().nullable(),
    budget: MoneySchema.nullable(),
    dryRun: z.boolean(),
  })
  .partial();
export type UpdateStrategy = z.infer<typeof UpdateStrategySchema>;

/** `GET /api/strategies/:id` and lifecycle-action responses. */
export const StrategyResponseSchema = StrategySchema;
export type StrategyResponse = z.infer<typeof StrategyResponseSchema>;

/** `GET /api/strategies/:id/runs`. */
export const ListRunsQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type ListRunsQuery = z.infer<typeof ListRunsQuerySchema>;

export const ListRunsResponseSchema = pageOf(StrategyRunSchema);
export type ListRunsResponse = z.infer<typeof ListRunsResponseSchema>;

/** `GET /api/strategies/:id/runs/:runId/decisions`. */
export const RunDecisionsParamSchema = z.object({
  id: z.string().min(1),
  runId: z.string().min(1),
});
export type RunDecisionsParam = z.infer<typeof RunDecisionsParamSchema>;

export const RunDecisionsResponseSchema = z.object({
  run: StrategyRunSchema,
  decisions: z.array(DecisionSchema),
});
export type RunDecisionsResponse = z.infer<typeof RunDecisionsResponseSchema>;

/** `GET /api/strategies/:id/pnl`. */
export const StrategyPnlResponseSchema = StrategyPnlSchema;
export type StrategyPnlResponse = z.infer<typeof StrategyPnlResponseSchema>;

/** `GET /api/strategies/:id/export`. */
export const StrategyExportResponseSchema = StrategyExportSchema;
export type StrategyExportResponse = z.infer<typeof StrategyExportResponseSchema>;

/** `POST /api/strategies/import`. */
export const ImportStrategySchema = z.object({
  export: StrategyExportSchema,
  name: z.string().min(1).optional(),
});
export type ImportStrategy = z.infer<typeof ImportStrategySchema>;

/** `GET /api/strategies/presets`. */
export const ListPresetsResponseSchema = z.object({
  presets: z.array(PresetSummarySchema),
});
export type ListPresetsResponse = z.infer<typeof ListPresetsResponseSchema>;

/** `POST /api/strategies/presets/:presetId/install`. */
export const PresetIdParamSchema = z.object({ presetId: z.string().min(1) });
export type PresetIdParam = z.infer<typeof PresetIdParamSchema>;

export const InstallPresetSchema = z.object({
  name: z.string().min(1).optional(),
  accountSeq: z.string().optional(),
  budget: MoneySchema.optional(),
});
export type InstallPreset = z.infer<typeof InstallPresetSchema>;
